import type { ExternalArtist, IngestStatus, ReviewStatus } from '../api/externalCatalogApi'

const pill = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium'

/**
 * Is this one of our artists? A suggestion is styled as a question, not a fact: it is a name
 * match nobody has confirmed, and it must not read as "linked" at a glance.
 */
export function MatchBadge({ artist }: { artist: ExternalArtist }) {
  if (artist.matchStatus === 'linked') {
    return (
      <span className={`${pill} bg-emerald-100 text-emerald-700`}>
        Ours{artist.artistName ? ` · ${artist.artistName}` : ''}
      </span>
    )
  }
  if (artist.matchStatus === 'suggested') {
    return (
      <span className={`${pill} border border-dashed border-amber-400 bg-amber-50 text-amber-800`}>
        Possibly {artist.suggestedArtistName ?? 'ours'}?
      </span>
    )
  }
  return <span className={`${pill} bg-slate-100 text-slate-600`}>Not in ARTHAUS</span>
}

const REVIEW_STYLES: Record<ReviewStatus, { label: string; className: string }> = {
  new: { label: 'Unreviewed', className: 'bg-slate-100 text-slate-600' },
  shortlisted: { label: 'Shortlisted', className: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-700' },
  declined: { label: 'Declined', className: 'bg-slate-700 text-white' },
}

export function ReviewBadge({ status }: { status: ReviewStatus }) {
  const s = REVIEW_STYLES[status] ?? REVIEW_STYLES.new
  return <span className={`${pill} ${s.className}`}>{s.label}</span>
}

const INGEST_STYLES: Record<IngestStatus, { label: string; className: string }> = {
  new: { label: 'Missing', className: 'bg-amber-100 text-amber-800' },
  queued: { label: 'Queued', className: 'bg-blue-100 text-blue-700' },
  ingesting: { label: 'Fetching', className: 'bg-blue-100 text-blue-700' },
  ingested: { label: 'Ingested', className: 'bg-emerald-100 text-emerald-700' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-700' },
  skipped: { label: 'Skipped', className: 'bg-slate-100 text-slate-600' },
  expired: { label: 'License ended', className: 'bg-slate-700 text-white' },
}

/** Where one mirrored artwork stands. "Held" wins over the ingest state: we have the piece. */
export function ArtworkStateBadge({
  artworkId,
  status,
  expired,
}: {
  artworkId?: number | null
  status?: IngestStatus
  expired?: boolean
}) {
  if (expired) return <span className={`${pill} ${INGEST_STYLES.expired.className}`}>License ended</span>
  if (artworkId && status !== 'ingested') {
    return <span className={`${pill} bg-emerald-50 text-emerald-700`}>In our catalog</span>
  }
  const s = INGEST_STYLES[status ?? 'new']
  return <span className={`${pill} ${s.className}`}>{s.label}</span>
}
